import React, { useEffect } from 'react';
import { PageBuilder } from '../components/PageBuilder';
import { useBuilderStore } from '../stores/builderStore';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';

const API_BASE_URL = import.meta.env.PROD
  ? 'https://admin.clarityofconcept.com/api'
  : 'http://localhost:8000';

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('admin_token');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

const HomepageBuilderIntegrated: React.FC = () => {
  // Override the store's save and publish methods to use backend API
  useEffect(() => {
    const originalSave = useBuilderStore.getState().save;
    const originalPublish = useBuilderStore.getState().publish;

    // Override save to use backend API
    useBuilderStore.setState({
      save: async (): Promise<void> => {
        const sections = useBuilderStore.getState().sections;
        try {
          const response = await fetch(`${API_BASE_URL}/homepage/builder/save`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              sections: sections.map(s => ({
                ...s,
                section_type: s.type,
                template_id: s.template_id || null,
                position: s.position,
                content: s.content?.map(c => ({
                  ...c,
                  component_type: c.component_type || 'text'
                }))
              })),
              layout_name: 'Homepage'
            }),
          });

          if (response.ok) {
            originalSave(); // Also save locally
            useBuilderStore.setState({ hasChanges: false });
          } else {
            throw new Error('Save failed');
          }
        } catch (error) {
          console.error('Backend save failed:', error);
          originalSave(); // Save locally as backup
          throw error;
        }
      },

      publish: async (): Promise<void> => {
        const sections = useBuilderStore.getState().sections;
        try {
          const response = await fetch(`${API_BASE_URL}/homepage/builder/publish`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              sections: sections.map(s => ({
                ...s,
                section_type: s.type,
                template_id: s.template_id || null,
                position: s.position,
                content: s.content?.map(c => ({
                  ...c,
                  component_type: c.component_type || 'text'
                }))
              })),
              layout_name: 'Homepage'
            }),
          });

          if (response.ok) {
            originalPublish();
            useBuilderStore.setState({ hasChanges: false });
          } else {
            throw new Error('Publish failed');
          }
        } catch (error) {
          console.error('Backend publish failed:', error);
          throw error;
        }
      }
    });

    // Load initial data from backend
    loadFromBackend();
    loadTemplatesFromBackend();

    // Cleanup - restore original methods on unmount
    return () => {
      useBuilderStore.setState({
        save: originalSave,
        publish: originalPublish
      });
    };
  }, []);

  const loadFromBackend = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/homepage/builder/current`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.sections && data.sections.length > 0) {
          // Transform backend data to match our store format
          const transformedSections = data.sections.map((s: any) => ({
            id: s.id?.toString() || `section-${Date.now()}-${Math.random()}`,
            type: s.section_type || s.type || 'hero',
            template_id: s.template_id,
            title: s.title || '',
            subtitle: s.subtitle || '',
            position: s.position || 0,
            is_visible: s.is_visible !== false,
            props: s.props || {},
            styles: s.styles || {},
            responsive: s.responsive || { desktop: true, tablet: true, mobile: true },
            animations: s.animations || {},
            content: s.content?.map((c: any) => ({
              id: c.id?.toString() || `content-${Date.now()}-${Math.random()}`,
              component_type: c.component_type || c.content_type || 'text',
              title: c.title,
              description: c.description,
              image_url: c.image_url,
              thumbnail_url: c.thumbnail_url,
              props: c.props || {},
              styles: c.styles || {},
              position: c.position || 0,
              is_visible: c.is_visible !== false
            })) || []
          }));

          useBuilderStore.setState({
            sections: transformedSections,
            hasChanges: false
          });

          toast.success('Homepage loaded from server');
        }
      }
    } catch (error) {
      console.error('Failed to load from backend:', error);
      // Load from localStorage as fallback
      const localSections = JSON.parse(localStorage.getItem('homepage-sections') || '[]');
      if (localSections.length > 0) {
        useBuilderStore.getState().loadSections(localSections);
      }
    }
  };

  const loadTemplatesFromBackend = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/homepage/builder/templates`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const templates = await response.json();
        if (templates && templates.length > 0) {
          // Transform templates to match our format
          const transformedTemplates = templates.map((t: any) => ({
            id: t.id?.toString() || `template-${Date.now()}`,
            category: t.category || 'general',
            name: t.name || 'Untitled Template',
            description: t.description,
            thumbnail: t.thumbnail,
            config: t.config || {},
            is_premium: t.is_premium || false
          }));

          useBuilderStore.setState({ templates: transformedTemplates });
        }
      }
    } catch (error) {
      console.error('Failed to load templates from backend:', error);
      // Use default templates as fallback
      const store = useBuilderStore.getState();
      if (store.loadTemplates) {
        store.loadTemplates();
      }
    }
  };

  return (
    <>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#10B981',
            },
          },
          error: {
            style: {
              background: '#EF4444',
            },
          },
        }}
      />
      <div className="h-screen">
        <PageBuilder />
      </div>
    </>
  );
};

export default HomepageBuilderIntegrated;